import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, EyeOff, Trash2, ExternalLink } from 'lucide-react';

/**
 * Component to view and manage collection data stored in localStorage
 * Useful for debugging and understanding what data is available from Pricing Explorer
 */
export const CollectionDataViewer: React.FC = () => {
  const [collectionData, setCollectionData] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const loadCollectionData = () => {
      try {
        const stored = localStorage.getItem('pricing_explorer_collection');
        if (stored) {
          setCollectionData(JSON.parse(stored));
        }
      } catch (error) {
        console.error('Error loading collection data:', error);
      }
    };

    loadCollectionData();
    
    // Check for updates periodically
    const interval = setInterval(loadCollectionData, 2000);
    return () => clearInterval(interval);
  }, []);

  const handleClearData = () => {
    localStorage.removeItem('pricing_explorer_collection');
    setCollectionData(null);
  };

  const handleOpenAsset = (imageUrl: string) => {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  };

  if (!collectionData) {
    return null;
  }

  return (
    <Card className="fixed bottom-4 right-4 w-80 shadow-lg border-2 border-purple-200 bg-white/95 backdrop-blur z-50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">Collection Data Available</CardTitle>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(!isVisible)}
            >
              {isVisible ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearData}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      {isVisible && (
        <CardContent className="pt-0">
          <div className="space-y-3">
            <div>
              <h4 className="font-medium text-sm">{collectionData.name}</h4>
              {collectionData.description && (
                <p className="text-xs text-gray-600 mt-1">{collectionData.description}</p>
              )}
            </div>
            
            {collectionData.templateType && (
              <Badge variant="secondary" className="text-xs">
                {collectionData.templateType} template
              </Badge>
            )}
            
            <div>
              <p className="text-xs font-medium mb-2">
                {collectionData.assets?.length || 0} Assets Available:
              </p>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {collectionData.assets?.map((asset: any, index: number) => (
                  <div key={index} className="flex items-center gap-2 p-1 bg-gray-50 rounded text-xs">
                    <span className="flex-1 truncate">{asset.header || `Asset ${index + 1}`}</span>
                    {asset.imageUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => handleOpenAsset(asset.imageUrl)}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )) || <p className="text-xs text-gray-500">No assets</p>}
              </div>
            </div>
            
            <div className="text-xs text-gray-500 border-t pt-2">
              <p>Stored: {new Date(collectionData.timestamp).toLocaleTimeString()}</p>
              <p>You can now manually add these assets to your Canva design</p>
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};